import { useState, useEffect, useCallback } from 'react';
import CommonLayout from '../../../layouts/CommonLayout';
import { Modal, Button, Form, Pagination } from 'react-bootstrap';
import { allAPiServicesCall } from '../../../services/apiServices';
import { getAuthConfig } from '../../../services/apiUtils';
import { toast } from 'react-toastify';
import './AlbumContributionsList.css';
import { extractObjectId, formatDate, formatDateTime } from '../../../utils/utils';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { FaCompactDisc, FaCheck, FaTimes, FaEye, FaTrash, FaMusic, FaImage } from 'react-icons/fa';

const AlbumContributionsList = () => {
    const [contributions, setContributions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedContribution, setSelectedContribution] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [processing, setProcessing] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const itemsPerPage = 10;

    // Bulk selection state
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkRejectionReason, setBulkRejectionReason] = useState('');
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkAction, setBulkAction] = useState('');

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);

    const fetchContributions = useCallback(async (page = 1, status = '') => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: itemsPerPage,
            };
            if (status) {
                params.status = status;
            }
            const response = await allAPiServicesCall.getAllAlbumContributions(params, getAuthConfig());
            if (response?.status === 200) {
                setContributions(response.data || []);
                if (response.pagination) {
                    setCurrentPage(response.pagination.currentPage);
                    setTotalPages(response.pagination.totalPages);
                    setTotalCount(response.pagination.totalCount);
                }
            } else {
                setContributions([]);
            }
        } catch (error) {
            console.error("Error fetching contributions:", error);
            toast.error('Error fetching contributions');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchContributionDetails = async (id) => {
        const contributionId = extractObjectId(id);
        setLoading(true);
        try {
            const response = await allAPiServicesCall.getAlbumContributionById(getAuthConfig(), contributionId);
            if (response?.data) {
                setSelectedContribution(response.data);
                setModalOpen(true);
            }
        } catch (error) {
            toast.error('Error fetching contribution details');
        } finally {
            setLoading(false);
        }
    };

    const handleApproveOrReject = async (id, status) => {
        if (status === 'rejected' && !rejectionReason.trim()) {
            toast.warning('Please provide a reason for rejection');
            return;
        }

        setProcessing(true);
        try {
            const payload = {
                contributionId: extractObjectId(id),
                status: status,
                ...(status === 'rejected' && { rejectionReason: rejectionReason.trim() }),
            };
            await allAPiServicesCall.processAlbumContribution(payload, getAuthConfig());

            setModalOpen(false);
            setRejectionReason('');
            fetchContributions(currentPage, statusFilter);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setProcessing(false);
        }
    };

    const openDeleteModal = (id) => {
        setDeleteTargetId(id);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!deleteTargetId) return;

        setProcessing(true);
        try {
            const contributionId = extractObjectId(deleteTargetId);
            await allAPiServicesCall.deleteAlbumContribution({ id: contributionId }, getAuthConfig());
            setShowDeleteModal(false);
            setDeleteTargetId(null);
            fetchContributions(currentPage, statusFilter);
        } catch (error) {
            toast.error('Error deleting contribution');
        } finally {
            setProcessing(false);
        }
    };

    // Bulk selection handlers
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const pendingIds = contributions
                .filter(c => c.status === 'pending')
                .map(c => extractObjectId(c._id));
            setSelectedIds(pendingIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id) => {
        const extractedId = extractObjectId(id);
        setSelectedIds(prev => {
            if (prev.includes(extractedId)) {
                return prev.filter(i => i !== extractedId);
            } else {
                return [...prev, extractedId];
            }
        });
    };

    const handleBulkAction = async () => {
        if (selectedIds.length === 0) {
            toast.warning('Please select at least one contribution');
            return;
        }

        if (bulkAction === 'rejected' && !bulkRejectionReason.trim()) {
            toast.warning('Please provide a reason for rejection');
            return;
        }

        setProcessing(true);
        try {
            const payload = {
                contributionIds: selectedIds,
                status: bulkAction,
                ...(bulkAction === 'rejected' && { rejectionReason: bulkRejectionReason.trim() }),
            };
            await allAPiServicesCall.bulkProcessAlbumContributions(payload, getAuthConfig());

            setShowBulkModal(false);
            setBulkRejectionReason('');
            setSelectedIds([]);
            setBulkAction('');
            fetchContributions(currentPage, statusFilter);
        } catch (error) {
            toast.error(error.message || 'Error processing bulk action');
        } finally {
            setProcessing(false);
        }
    };

    const openBulkModal = (action) => {
        if (selectedIds.length === 0) {
            toast.warning('Please select at least one pending contribution');
            return;
        }
        setBulkAction(action);
        setShowBulkModal(true);
    };

    const getStatusBadge = (status) => {
        const statusStyles = {
            pending: { backgroundColor: '#ffc107', color: '#000' },
            approved: { backgroundColor: '#28a745', color: '#fff' },
            rejected: { backgroundColor: '#dc3545', color: '#fff' },
        };
        return (
            <span
                className="badge px-2 py-1 rounded"
                style={statusStyles[status] || statusStyles.pending}
            >
                {status?.charAt(0).toUpperCase() + status?.slice(1)}
            </span>
        );
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        setSelectedIds([]);
        fetchContributions(page, statusFilter);
    };

    const handleStatusFilterChange = (e) => {
        const newStatus = e.target.value;
        setStatusFilter(newStatus);
        setCurrentPage(1);
        setSelectedIds([]);
        fetchContributions(1, newStatus);
    };

    // Generate pagination items
    const renderPagination = () => {
        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        items.push(
            <Pagination.First key="first" onClick={() => handlePageChange(1)} disabled={currentPage === 1} />,
            <Pagination.Prev key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
        );

        if (startPage > 1) {
            items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
        }

        items.push(
            <Pagination.Next key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />,
            <Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
        );

        return items;
    };

    useEffect(() => {
        fetchContributions(1, '');
    }, [fetchContributions]);

    const pendingCount = contributions.filter(c => c.status === 'pending').length;

    return (
        <CommonLayout>
            <div className="album-contributions-container">
                {/* Premium Header */}
                <div className="ac-header">
                    <div className="ac-header-top">
                        <div className="ac-title-wrapper">
                            <div className="ac-title-icon">
                                <FaCompactDisc />
                            </div>
                            <div className="ac-title-text">
                                <h1 className="ac-title">Album Contributions</h1>
                                <p className="ac-subtitle">Review and approve albums submitted by users</p>
                            </div>
                        </div>
                        <div className="ac-stats">
                            <div className="ac-stat-badge">
                                <FaCompactDisc />
                                <span>Total: <strong>{totalCount}</strong></span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter and Bulk Actions Bar */}
                <div className="ac-filter-section">
                    <div className="ac-filter-left">
                        <select
                            className="ac-filter-select"
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                        >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                        <div className="ac-filter-info">
                            <span>Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
                        </div>
                    </div>

                    {/* Bulk Action Buttons */}
                    <div className="ac-filter-right">
                        <button
                            className="ac-bulk-btn ac-bulk-btn-approve"
                            onClick={() => openBulkModal('approved')}
                            disabled={selectedIds.length === 0}
                        >
                            <FaCheck /> Bulk Approve ({selectedIds.length})
                        </button>
                        <button
                            className="ac-bulk-btn ac-bulk-btn-reject"
                            onClick={() => openBulkModal('rejected')}
                            disabled={selectedIds.length === 0}
                        >
                            <FaTimes /> Bulk Reject ({selectedIds.length})
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="ac-table-card">
                        <div className="ac-loading">
                            <div className="ac-loading-spinner"></div>
                            <p className="ac-loading-text">Loading contributions...</p>
                        </div>
                    </div>
                ) : contributions.length > 0 ? (
                    <div className="ac-table-card">
                        <div className="ac-table-container">
                            <table className="ac-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <input
                                                type="checkbox"
                                                className="ac-checkbox"
                                                onChange={handleSelectAll}
                                                checked={pendingCount > 0 && selectedIds.length === pendingCount}
                                                disabled={pendingCount === 0}
                                                title="Select all pending contributions"
                                            />
                                        </th>
                                        <th>Cover</th>
                                        <th>Album</th>
                                        <th>Artist</th>
                                        <th>Type</th>
                                        <th>Year</th>
                                        <th>Tracks</th>
                                        <th>Contributed By</th>
                                        <th>Status</th>
                                        <th>Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contributions.map((contribution) => (
                                        <tr key={contribution._id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="ac-checkbox"
                                                    checked={selectedIds.includes(extractObjectId(contribution._id))}
                                                    onChange={() => handleSelectOne(contribution._id)}
                                                    disabled={contribution.status !== 'pending'}
                                                />
                                            </td>
                                            <td>
                                                {contribution.coverImageUrl ? (
                                                    <img
                                                        src={contribution.coverImageUrl}
                                                        alt={contribution.albumName}
                                                        className="ac-cover-preview"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : (
                                                    <div className="ac-cover-placeholder">
                                                        <FaImage />
                                                    </div>
                                                )}
                                            </td>
                                            <td><span className="ac-album-name">{contribution.albumName || 'Unknown Album'}</span></td>
                                            <td><span className="ac-artist-name">{contribution.artistName || 'Unknown Artist'}</span></td>
                                            <td>
                                                <span className={`ac-type-badge ${contribution.albumType}`}>
                                                    {contribution.albumType || 'Album'}
                                                </span>
                                            </td>
                                            <td><span className="ac-date">{contribution.releaseYear || 'N/A'}</span></td>
                                            <td>
                                                {contribution.tracklist?.length > 0 ? (
                                                    <span className="ac-track-count">
                                                        <FaMusic /> {contribution.tracklist.length}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td><span className="ac-contributor">{contribution.user_data?.username || 'N/A'}</span></td>
                                            <td>
                                                <span className={`ac-status-badge ${contribution.status}`}>
                                                    {contribution.status?.charAt(0).toUpperCase() + contribution.status?.slice(1)}
                                                </span>
                                            </td>
                                            <td><span className="ac-date">{formatDate(contribution.createdAt)}</span></td>
                                            <td>
                                                <div className="ac-actions">
                                                    <button
                                                        className="ac-action-btn ac-action-btn-view"
                                                        onClick={() => fetchContributionDetails(contribution._id)}
                                                        disabled={loading}
                                                    >
                                                        <FaEye /> View
                                                    </button>
                                                    <button
                                                        className="ac-action-btn ac-action-btn-delete"
                                                        onClick={() => openDeleteModal(contribution._id)}
                                                        disabled={loading}
                                                    >
                                                        <FaTrash /> Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="ac-pagination-wrapper">
                                <Pagination>{renderPagination()}</Pagination>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="ac-table-card">
                        <div className="ac-empty">
                            <div className="ac-empty-icon">
                                <FaCompactDisc />
                            </div>
                            <p className="ac-empty-text">No album contributions found.</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {modalOpen && selectedContribution && (
                <Modal show={modalOpen} onHide={() => { setModalOpen(false); setRejectionReason(''); }} size="lg" centered className="contribution-detail-modal">
                    <Modal.Header className="contribution-detail-header">
                        <div className="contribution-detail-header-content">
                            <div className="contribution-detail-icon">
                                <FaCompactDisc size={28} />
                            </div>
                            <div>
                                <Modal.Title className="contribution-detail-title">
                                    {selectedContribution.albumName || 'Album Contribution'}
                                </Modal.Title>
                                <p className="contribution-detail-subtitle">
                                    by {selectedContribution.artistName || 'Unknown Artist'}
                                </p>
                            </div>
                        </div>
                        <button className="contribution-detail-close" onClick={() => { setModalOpen(false); setRejectionReason(''); }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </Modal.Header>
                    <Modal.Body className="contribution-detail-body">
                        {/* Info Cards */}
                        <div className="contribution-info-grid">
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Album Type</span>
                                <span className="contribution-info-value" style={{ textTransform: 'capitalize' }}>{selectedContribution.albumType || 'Album'}</span>
                            </div>
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Release Year</span>
                                <span className="contribution-info-value">{selectedContribution.releaseYear || 'N/A'}</span>
                            </div>
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Release Date</span>
                                <span className="contribution-info-value">{selectedContribution.releaseDate ? formatDate(selectedContribution.releaseDate) : 'Not specified'}</span>
                            </div>
                        </div>
                        <div className="contribution-info-grid">
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Status</span>
                                <span className="contribution-info-value">{getStatusBadge(selectedContribution.status)}</span>
                            </div>
                        </div>

                        <div className="contribution-info-grid">
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Contributed By</span>
                                <span className="contribution-info-value">{selectedContribution.user_data?.username || 'Unknown'}</span>
                                <span className="contribution-info-email">{selectedContribution.user_data?.email || ''}</span>
                            </div>
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Genre</span>
                                <span className="contribution-info-value">{selectedContribution.genre || 'Not specified'}</span>
                            </div>
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Submitted</span>
                                <span className="contribution-info-value">{formatDate(selectedContribution.createdAt)}</span>
                                <span className="contribution-info-email">{formatDateTime(selectedContribution.createdAt, '').split(', ')[1] || ''}</span>
                            </div>
                        </div>

                        {/* Cover Image */}
                        {selectedContribution.coverImageUrl && (
                            <div className="contribution-section">
                                <div className="contribution-section-header">
                                    <span className="contribution-section-icon current">
                                        <FaImage />
                                    </span>
                                    <span className="contribution-section-title">Cover Image</span>
                                </div>
                                <div style={{ textAlign: 'center', padding: '1rem' }}>
                                    <img
                                        src={selectedContribution.coverImageUrl}
                                        alt="Album Cover"
                                        style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)' }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Tracklist */}
                        {selectedContribution.tracklist?.length > 0 && (
                            <div className="contribution-section">
                                <div className="contribution-section-header">
                                    <span className="contribution-section-icon new">
                                        <FaMusic />
                                    </span>
                                    <span className="contribution-section-title">Tracklist ({selectedContribution.tracklist.length} tracks)</span>
                                </div>
                                <div className="ac-tracklist-section">
                                    {selectedContribution.tracklist.map((track, index) => (
                                        <div key={index} className="ac-tracklist-item">
                                            <span className="ac-track-number">{index + 1}</span>
                                            <span className="ac-track-name">{track}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}


                        {/* Rejection Reason (if rejected) */}
                        {selectedContribution.status === 'rejected' && selectedContribution.rejectionReason && (
                            <div className="contribution-section">
                                <div className="contribution-section-header">
                                    <span className="contribution-section-icon rejected">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="15" y1="9" x2="9" y2="15"></line>
                                            <line x1="9" y1="9" x2="15" y2="15"></line>
                                        </svg>
                                    </span>
                                    <span className="contribution-section-title">Rejection Reason</span>
                                </div>
                                <div className="contribution-lyrics-box rejected">
                                    {selectedContribution.rejectionReason}
                                </div>
                            </div>
                        )}

                        {/* Rejection Input (if pending) */}
                        {selectedContribution.status === 'pending' && (
                            <div className="contribution-section">
                                <div className="contribution-section-header">
                                    <span className="contribution-section-icon rejected">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                        </svg>
                                    </span>
                                    <span className="contribution-section-title">Rejection Reason (if rejecting)</span>
                                </div>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    placeholder="Enter reason for rejection..."
                                    className="contribution-rejection-input"
                                />
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="contribution-detail-footer">
                        {selectedContribution.status === 'pending' && (
                            <>
                                <Button
                                    className="contribution-action-btn approve"
                                    onClick={() => handleApproveOrReject(selectedContribution._id, 'approved')}
                                    disabled={processing}
                                >
                                    {processing ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <FaCheck className="me-2" />
                                            Approve & Create Album
                                        </>
                                    )}
                                </Button>
                                <Button
                                    className="contribution-action-btn reject"
                                    onClick={() => handleApproveOrReject(selectedContribution._id, 'rejected')}
                                    disabled={processing}
                                >
                                    {processing ? 'Processing...' : (
                                        <>
                                            <FaTimes className="me-2" />
                                            Reject
                                        </>
                                    )}
                                </Button>
                            </>
                        )}
                        <Button
                            className="contribution-action-btn close-btn"
                            onClick={() => { setModalOpen(false); setRejectionReason(''); }}
                            disabled={processing}
                        >
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Bulk Action Modal */}
            <ConfirmationModal
                show={showBulkModal}
                handleClose={() => { setShowBulkModal(false); setBulkRejectionReason(''); }}
                onConfirm={handleBulkAction}
                title={`Bulk ${bulkAction === 'approved' ? 'Approve' : 'Reject'} Album Contributions`}
                message={`You are about to ${bulkAction === 'approved' ? 'approve' : 'reject'} ${selectedIds.length} album contribution(s).${bulkAction === 'approved' ? ' New albums will be created immediately.' : ''}`}
                confirmText={`Confirm ${bulkAction === 'approved' ? 'Approve' : 'Reject'}`}
                cancelText="Cancel"
                variant={bulkAction === 'approved' ? 'success' : 'danger'}
                isLoading={processing}
                confirmDisabled={bulkAction === 'rejected' && !bulkRejectionReason.trim()}
            >
                {bulkAction === 'rejected' && (
                    <Form.Group>
                        <Form.Label><strong>Rejection Reason (required):</strong></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={bulkRejectionReason}
                            onChange={(e) => setBulkRejectionReason(e.target.value)}
                            placeholder="Enter reason for bulk rejection..."
                        />
                    </Form.Group>
                )}
            </ConfirmationModal>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                show={showDeleteModal}
                handleClose={() => { setShowDeleteModal(false); setDeleteTargetId(null); }}
                onConfirm={handleDelete}
                title="Delete Album Contribution"
                message="Are you sure you want to delete this album contribution? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
                isLoading={processing}
            />
        </CommonLayout>
    );
};

export default AlbumContributionsList;
